/** Create / edit an invite link (react-hook-form + zod).
 *
 *  Create: label, an optional e-mail (a bound link, with "Send now"), an optional expiry, the
 *  quantity (1–100 unbound links in one batch, D14), and the context — allowed booking systems,
 *  "Let the client choose the tenant code" (D15), promo code, contract reference and the
 *  "Advanced context" JSON editor (known keys checked, other keys kept and flagged). The 201
 *  carries the new links, so it is called directly (never through `useMutation`, whose cache
 *  keeps the answer) and handed straight to the result dialog.
 *
 *  Edit (until used): the same fields minus quantity and Send now; only what changed is sent.
 *  The server's per-field refusals land under their fields. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import * as invitesApi from '@/api/onboardingInvites';
import type { InviteCreateResponse, InviteListItem, InviteUpdate } from '@/api/onboardingInvites';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';

import { INVITE_HELP } from './fieldHelp';
import { inviteMarketplaceText } from './inviteFormat';
import {
  CONTRACT_REF_MAX,
  PROMO_CODE_MAX,
  analyseContext,
  sameJson,
  splitContext,
  type StructuredContext,
} from './inviteContext';

/** A booking system the self-signup could offer (the easy-onboarding registry). */
export interface RestechOption {
  code: string;
  available: boolean;
}

// The backend's own check is Django's validate_email; this only catches typos early.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormValues {
  label: string;
  email: string;
  send: boolean;
  quantity: string;
  expires: Date | null;
  restechs: string[];
  allowCodeEdit: boolean;
  promoCode: string;
  contractRef: string;
  advanced: string;
}

const structuredOf = (v: FormValues): StructuredContext => ({
  restechs: v.restechs,
  allowCodeEdit: v.allowCodeEdit,
  promoCode: v.promoCode,
  contractRef: v.contractRef,
});

function schemaFor(mode: 'create' | 'edit', restechCodes: readonly string[] | null, fixed: Record<string, unknown>) {
  return z
    .object({
      label: z.string().trim().min(1, 'A label is required.').max(invitesApi.LABEL_MAX, `At most ${invitesApi.LABEL_MAX} characters.`),
      email: z.string().trim().refine((v) => v === '' || EMAIL_RE.test(v), 'A valid e-mail address.'),
      send: z.boolean(),
      quantity: z.string(),
      expires: z.date().nullable(),
      restechs: z.array(z.string()),
      allowCodeEdit: z.boolean(),
      promoCode: z.string().trim().max(PROMO_CODE_MAX, `At most ${PROMO_CODE_MAX} characters.`),
      contractRef: z.string().trim().max(CONTRACT_REF_MAX, `At most ${CONTRACT_REF_MAX} characters.`),
      advanced: z.string(),
    })
    .superRefine((v, ctx) => {
      if (mode === 'create') {
        const q = Number(v.quantity);
        if (!Number.isInteger(q) || q < 1 || q > invitesApi.MAX_QUANTITY) {
          ctx.addIssue({ code: 'custom', path: ['quantity'], message: `1 to ${invitesApi.MAX_QUANTITY}.` });
        } else if (q > 1 && v.email.trim()) {
          ctx.addIssue({ code: 'custom', path: ['quantity'], message: 'A bound link (with an e-mail) is created alone.' });
        }
        // "Send now" without an e-mail is simply not sent (the box is disabled then).
      }
      if (v.expires && Number.isNaN(v.expires.getTime())) {
        ctx.addIssue({ code: 'custom', path: ['expires'], message: 'A date and a time.' });
      } else if (mode === 'create' && v.expires && v.expires.getTime() <= Date.now()) {
        ctx.addIssue({ code: 'custom', path: ['expires'], message: 'In the future.' });
      }
      // Create only: an edit sends the context only when it changed, and the server checks it then —
      // a code the registry dropped since must not block an unrelated edit.
      if (restechCodes && mode === 'create') {
        const unknown = v.restechs.map((r) => r.trim().toLowerCase()).find((r) => r && !restechCodes.includes(r));
        if (unknown) {
          ctx.addIssue({ code: 'custom', path: ['restechs'], message: `Unknown booking system '${unknown}'.` });
        }
      }
      const a = analyseContext(v.advanced, structuredOf(v), restechCodes, fixed);
      if (!a.context) {
        ctx.addIssue({
          code: 'custom',
          path: ['advanced'],
          message: a.advancedError ?? 'Fix the keys listed below.',
        });
      }
    });
}

function emptyValues(): FormValues {
  return {
    label: '',
    email: '',
    send: false,
    quantity: '1',
    expires: null,
    restechs: [],
    allowCodeEdit: false,
    promoCode: '',
    contractRef: '',
    advanced: '',
  };
}

function valuesOf(invite: InviteListItem): FormValues {
  const { structured, advancedText } = splitContext(invite.context);
  return {
    ...emptyValues(),
    label: invite.label,
    email: invite.email ?? '',
    expires: invite.expires_ts ? new Date(invite.expires_ts) : null,
    restechs: structured.restechs,
    allowCodeEdit: structured.allowCodeEdit,
    promoCode: structured.promoCode,
    contractRef: structured.contractRef,
    advanced: advancedText,
  };
}

/** A server field error → the form field that shows it. */
const SERVER_FIELD: Record<string, keyof FormValues> = {
  label: 'label',
  email: 'email',
  expires_ts: 'expires',
  quantity: 'quantity',
  send: 'send',
  context: 'advanced',
  'context.restechs': 'restechs',
  'context.allow_code_edit': 'allowCodeEdit',
  'context.promo_code': 'promoCode',
  'context.contract_ref': 'contractRef',
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Edit this invite; absent = create. */
  invite?: InviteListItem | null;
  /** The booking systems to offer; null when the registry could not be read (free entry). */
  restechOptions: RestechOption[] | null;
  /** Create: the 201 (with the links) for the result dialog. */
  onCreated?: (result: InviteCreateResponse) => void;
}

export function InviteFormDialog({ open, onClose, invite, restechOptions, onCreated }: Props) {
  const mode: 'create' | 'edit' = invite ? 'edit' : 'create';
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const restechCodes = useMemo(() => restechOptions?.map((r) => r.code) ?? null, [restechOptions]);
  // An existing invite's read-only context keys (a marketplace install's record): never edited,
  // sent back unchanged whenever the context is sent.
  const fixed = useMemo(() => (invite ? splitContext(invite.context).fixed : {}), [invite]);
  const installedBy = invite ? inviteMarketplaceText(invite.context) : null;
  const schema = useMemo(() => schemaFor(mode, restechCodes, fixed), [mode, restechCodes, fixed]);
  const [pending, setPending] = useState(false);
  const [serverKeyErrors, setServerKeyErrors] = useState<Record<string, string>>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Set again in the body: StrictMode runs the cleanup once on a live component.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues() });

  useEffect(() => {
    if (!open) return;
    reset(invite ? valuesOf(invite) : emptyValues());
    setServerKeyErrors({});
    setAdvancedOpen(Boolean(invite && splitContext(invite.context).advancedText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invite?.id]);

  const watched = useWatch({ control });
  const email = (watched.email ?? '').trim();
  const analysis = useMemo(
    () =>
      analyseContext(
        watched.advanced ?? '',
        {
          restechs: watched.restechs ?? [],
          allowCodeEdit: watched.allowCodeEdit ?? false,
          promoCode: watched.promoCode ?? '',
          contractRef: watched.contractRef ?? '',
        },
        restechCodes,
        fixed,
      ),
    [watched.advanced, watched.restechs, watched.allowCodeEdit, watched.promoCode, watched.contractRef, restechCodes, fixed],
  );
  const keyErrors = { ...serverKeyErrors, ...analysis.keyErrors };
  // A problem in the advanced context is never hidden in a closed section.
  const advancedError = errors.advanced?.message;
  useEffect(() => {
    if (advancedError) setAdvancedOpen(true);
  }, [advancedError]);

  const applyServerErrors = (e: unknown) => {
    const fields = invitesApi.inviteFieldErrors(e);
    const unmapped: Record<string, string> = {};
    for (const [field, message] of Object.entries(fields)) {
      const target = SERVER_FIELD[field];
      if (target) setError(target, { type: 'server', message });
      else if (field.startsWith('context.')) unmapped[field.slice('context.'.length)] = message;
    }
    setServerKeyErrors(unmapped);
    if (Object.keys(unmapped).length) setError('advanced', { type: 'server', message: 'Fix the keys listed below.' });
  };

  const submit = handleSubmit(async (v) => {
    const a = analyseContext(v.advanced, structuredOf(v), restechCodes, fixed);
    if (!a.context) return; // the resolver already refused it
    setServerKeyErrors({});
    setPending(true);
    try {
      if (mode === 'create') {
        const result = await invitesApi.create({
          label: v.label.trim(),
          email: v.email.trim() || null,
          expires_ts: v.expires ? v.expires.toISOString() : null,
          context: a.context,
          quantity: Number(v.quantity),
          send: v.send && Boolean(v.email.trim()),
        });
        void qc.invalidateQueries({ queryKey: invitesApi.invitesKeys.all });
        onCreated?.(result);
        onClose();
        return;
      }
      const original = invite!;
      const body: InviteUpdate = {};
      if (v.label.trim() !== original.label) body.label = v.label.trim();
      if ((v.email.trim() || null) !== (original.email ?? null)) body.email = v.email.trim() || null;
      const before = original.expires_ts ? new Date(original.expires_ts).getTime() : null;
      if ((v.expires?.getTime() ?? null) !== before) body.expires_ts = v.expires ? v.expires.toISOString() : null;
      if (!sameJson(a.context, original.context ?? {})) body.context = a.context;
      if (Object.keys(body).length === 0) {
        snackbar.show('Nothing changed.', 'info');
        onClose();
        return;
      }
      await invitesApi.update(original.id, body);
      void qc.invalidateQueries({ queryKey: invitesApi.invitesKeys.all });
      snackbar.show('Invite updated.', 'success');
      onClose();
    } catch (e) {
      if (!alive.current) return;
      applyServerErrors(e);
      snackbar.show(
        invitesApi.inviteErrorMessage(e, mode === 'create' ? 'The invite could not be created.' : 'The invite could not be updated.'),
        'error',
      );
      if (mode === 'edit') void qc.invalidateQueries({ queryKey: invitesApi.invitesKeys.all });
    } finally {
      if (alive.current) setPending(false);
    }
  });

  const quantity = Number(watched.quantity);
  const bulk = mode === 'create' && Number.isInteger(quantity) && quantity > 1;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      disableBackdropClose
      title={
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <span>{mode === 'create' ? 'New invite link' : `Edit invite #${invite?.id}`}</span>
          <HelpLink story="onboarding-invites" newTab />
        </Stack>
      }
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={pending} onClick={() => void submit()}>
            {mode === 'create'
              ? pending
                ? 'Creating…'
                : bulk
                  ? `Create ${quantity} invites`
                  : 'Create invite'
              : pending
                ? 'Saving…'
                : 'Save'}
          </Button>
        </>
      }
    >
      <Stack
        spacing={2}
        sx={{ mt: 1 }}
        component="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {mode === 'edit' && invite?.status === 'claimed' && (
          <Alert severity="info">
            A browser holds this link. Changes apply to its next step; the e-mail, if changed, becomes the admin e-mail.
          </Alert>
        )}
        <TextField
          {...register('label')}
          size="small"
          label="Label"
          placeholder="e.g. ACME Tours — contract Nov 2026, ITB 2027 booth #12"
          error={Boolean(errors.label)}
          helperText={errors.label?.message ?? 'Shown to the client and in the grid. Name the prospect or the occasion.'}
          inputProps={{ maxLength: invitesApi.LABEL_MAX }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
          <TextField
            {...register('email')}
            size="small"
            label="E-mail (optional)"
            placeholder="ops@prospect.example"
            sx={{ flex: 1 }}
            error={Boolean(errors.email)}
            helperText={errors.email?.message ?? (email ? 'Bound: the admin e-mail is fixed to it.' : 'Empty: unbound — the client verifies the e-mail they type.')}
            InputProps={{ endAdornment: <FieldHelp help={INVITE_HELP.email} /> }}
          />
          {mode === 'create' && (
            <Box sx={{ pt: 0.5 }}>
              <Controller
                control={control}
                name="send"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value && Boolean(email)}
                        disabled={!email}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Send now"
                  />
                )}
              />
              {errors.send && (
                <Typography variant="caption" color="error" display="block">
                  {errors.send.message}
                </Typography>
              )}
            </Box>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Controller
              control={control}
              name="expires"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Expires (optional)"
                    value={field.value}
                    onChange={(d) => field.onChange(d)}
                    disablePast={mode === 'create'}
                    slotProps={{
                      field: { clearable: true },
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: Boolean(errors.expires),
                        helperText: errors.expires?.message ?? 'Your local time. Empty: the link never expires.',
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Box>
          {mode === 'create' && (
            <TextField
              {...register('quantity')}
              size="small"
              label="Quantity"
              type="number"
              sx={{ width: { xs: '100%', sm: 180 } }}
              error={Boolean(errors.quantity)}
              helperText={errors.quantity?.message ?? (email ? 'With an e-mail: 1 (a bound link).' : `1–${invitesApi.MAX_QUANTITY} unbound links, one batch.`)}
              inputProps={{ min: 1, max: invitesApi.MAX_QUANTITY, step: 1 }}
              InputProps={{ endAdornment: <FieldHelp help={INVITE_HELP.quantity} /> }}
            />
          )}
        </Stack>

        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          Context
        </Typography>
        {installedBy && (
          <Alert severity="info" variant="outlined">
            Minted by a marketplace install: {installedBy}. Only the install sets this; it is kept as it is.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Controller
            control={control}
            name="restechs"
            render={({ field }) => (
              <Autocomplete<string, true, false, true>
                multiple
                freeSolo
                autoSelect
                sx={{ flexGrow: 1 }}
                options={restechOptions?.map((r) => r.code) ?? []}
                value={field.value}
                onChange={(_, next) => field.onChange([...new Set(next.map((r) => r.trim().toLowerCase()).filter(Boolean))])}
                getOptionLabel={(o) => o}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as typeof props & { key: string };
                  const meta = restechOptions?.find((r) => r.code === option);
                  return (
                    <li key={key} {...rest}>
                      {option}
                      {meta && !meta.available && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          not available here now
                        </Typography>
                      )}
                    </li>
                  );
                }}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    return <Chip key={key} size="small" label={tag} {...rest} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Allowed booking systems"
                    placeholder={field.value.length === 0 ? 'Every available one' : undefined}
                    error={Boolean(errors.restechs)}
                    helperText={
                      errors.restechs?.message ??
                      (restechOptions
                        ? 'Empty: every booking system the self-signup supports.'
                        : 'Type booking-system codes (e.g. fareharbor). Empty: every one.')
                    }
                  />
                )}
              />
            )}
          />
          <FieldHelp help={INVITE_HELP.restechs} />
        </Box>

        <Controller
          control={control}
          name="allowCodeEdit"
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
              label={labelWithHelp('Let the client choose the tenant code', INVITE_HELP.allowCodeEdit)}
            />
          )}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            {...register('promoCode')}
            size="small"
            label="Promo code (optional)"
            sx={{ flex: 1 }}
            error={Boolean(errors.promoCode)}
            helperText={errors.promoCode?.message ?? 'Kept and copied to the tenant.'}
          />
          <TextField
            {...register('contractRef')}
            size="small"
            label="Contract reference (optional)"
            sx={{ flex: 1 }}
            error={Boolean(errors.contractRef)}
            helperText={errors.contractRef?.message ?? 'Shown to ops.'}
          />
        </Stack>

        <Accordion
          disableGutters
          variant="outlined"
          expanded={advancedOpen}
          onChange={(_, next) => setAdvancedOpen(next)}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Advanced context</Typography>
            {analysis.uninterpreted.length > 0 && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${analysis.uninterpreted.length} not interpreted`}
                sx={{ ml: 1, height: 20 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <TextField
                {...register('advanced')}
                multiline
                minRows={3}
                maxRows={14}
                size="small"
                label="Other context keys (JSON object)"
                placeholder={'{"campaign": "ITB 2027"}'}
                error={Boolean(errors.advanced)}
                helperText={errors.advanced?.message ?? 'Known keys are checked; any other key is kept as it is.'}
                inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: 13 } }}
                InputProps={{ endAdornment: <FieldHelp help={INVITE_HELP.advanced} /> }}
              />
              {Object.keys(keyErrors).length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }} aria-label="Context key problems">
                  {Object.entries(keyErrors).map(([key, message]) => (
                    <Typography component="li" key={key} variant="caption" color="error">
                      <Box component="code">{key}</Box>: {message}
                    </Typography>
                  ))}
                </Box>
              )}
              {(analysis.interpreted.length > 0 || analysis.uninterpreted.length > 0) && (
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    The context sent:
                  </Typography>
                  {analysis.interpreted.map((k) => (
                    <Chip key={k} size="small" variant="outlined" color="info" label={`${k} · interpreted`} sx={{ height: 20 }} />
                  ))}
                  {analysis.uninterpreted.map((k) => (
                    <Chip key={k} size="small" variant="outlined" color="warning" label={`${k} · not interpreted`} sx={{ height: 20 }} />
                  ))}
                </Stack>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </AppDialog>
  );
}
